import { useMemo } from 'react';
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

import { RiCheckLine, RiFileCopyLine, RiLinkM } from '@remixicon/react';

import { Button, ButtonIcon } from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';

import { useAlert } from '../../hooks/useAlert';
import {
  type ClipboardInput,
  useCopyToClipboard,
} from '../../hooks/useCopyToClipboard';

type CopyOwnProps<T extends ElementType> = {
  value: ClipboardInput | (() => ClipboardInput);
  label?: string;
  labelCopied?: string;
  variant?: 'default' | 'compact';
  size?: 'small' | 'medium';
  className?: string;
  icon?: 'copy' | 'link';
  trackingId?: string;
  component?: T;
  resetMs?: number;
};

const DEFAULT_ICONS = {
  copy: RiFileCopyLine,
  link: RiLinkM,
};

export type CopyProps<T extends ElementType = 'button'> = CopyOwnProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof CopyOwnProps<T> | 'children'>;

const useCopyIndicator = ({
  copied,
  size,
  labelDefault,
  labelCopied,
  icon = 'copy',
}: {
  copied: boolean;
  size: NonNullable<CopyOwnProps<ElementType>['size']>;
  labelDefault: NonNullable<CopyOwnProps<ElementType>['label']>;
  labelCopied: NonNullable<CopyOwnProps<ElementType>['labelCopied']>;
  icon?: NonNullable<CopyOwnProps<ElementType>['icon']>;
}): { icon: ReactNode; label: string } =>
  useMemo(() => {
    const iconSize = size === 'small' ? 16 : 20;

    if (copied) {
      return {
        icon: (
          <RiCheckLine
            size={iconSize}
            aria-hidden
            data-testid="copy-icon-check"
          />
        ),
        label: labelCopied,
      };
    }

    const DefaultIcon = DEFAULT_ICONS[icon];

    return {
      icon: (
        <DefaultIcon
          size={iconSize}
          aria-hidden
          data-testid="copy-icon-default"
        />
      ),
      label: labelDefault,
    };
  }, [copied, size, labelDefault, labelCopied, icon]);

export const Copy = <T extends ElementType = 'button'>({
  value,
  component,
  resetMs,
  label: labelDefault = 'Copy to clipboard',
  labelCopied = 'Copied',
  variant = 'default',
  size = 'small',
  className,
  icon: iconVariant,
  trackingId,
  ...props
}: CopyProps<T>): ReactNode => {
  const alert = useAlert();
  const { copied, copy } = useCopyToClipboard(resetMs);

  const handleCopy = async () => {
    const resolved = typeof value === 'function' ? value() : value;
    if (!resolved) return;

    const didCopy = await copy(resolved);
    alert.post(
      didCopy
        ? {
            message: 'Copied to clipboard',
            severity: 'success',
            display: 'transient',
          }
        : {
            message: 'Could not copy to clipboard',
            severity: 'error',
            display: 'transient',
          },
    );
  };

  const { icon, label } = useCopyIndicator({
    copied,
    size,
    labelDefault,
    labelCopied,
    icon: iconVariant,
  });

  if (component) {
    const Component: ElementType = component;
    return (
      <Component
        {...props}
        icon={icon}
        className={className}
        onClick={handleCopy}
      >
        {label}
      </Component>
    );
  }

  if (variant === 'default') {
    return (
      <Button
        {...props}
        variant="secondary-bordered"
        size={size}
        className={className}
        onClick={handleCopy}
        data-track-id={trackingId}
        data-track-action={trackingId ? 'copy' : undefined}
      >
        {icon} {label}
      </Button>
    );
  }

  return (
    <TooltipRoot>
      <ButtonIcon
        {...props}
        variant="tertiary"
        size={size}
        className={className}
        aria-label={label}
        onClick={handleCopy}
        data-track-id={trackingId}
        data-track-action={trackingId ? 'copy' : undefined}
      >
        {icon}
      </ButtonIcon>
      <Tooltip>{label}</Tooltip>
    </TooltipRoot>
  );
};
